import React, { useCallback, useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  Button,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Expander,
  ExpanderContent,
  ExpanderHeader,
  H3,
  Paragraph,
  Separator,
  useOverlay,
  type UseOverlayReturnOptions,
} from '@fs/zion-ui'
import Cache from '@fs/zion-cache'
import { Link } from '@fs/zion-router'
import { css } from '@emotion/core'
import useSourceCentricDetailsFlag from '../../data-service/useSourceCentricDetailsFlag'

// TODO(TREEWEB-12092): replace with the real help article URL once it is published
const HELP_ARTICLE_URL = 'https://www.familysearch.org/en/help/helpcenter/'

const listCss = css`
  li {
    margin: 4px 20px;
  }
`

export const CACHE_KEY = 'summary-overlay'

// The overlay never opens before this date, and bumping the date past users' stored
// dismissal times shows the overlay again (e.g. whenever the What's New content changes)
export const WHATS_NEW_UPDATED_AT = Date.UTC(2026, 6, 23)

const year = 365 * 24 * 60 * 60 * 1000

export const dialogCache = new Cache<number>({
  lifetime: year, // without an explicit lifetime, zion-cache clears the whole store every two weeks
  storeName: 'sourceCentricDetails',
  type: 'local', // Use localStorage
})

const checkDialogState = async (open: UseOverlayReturnOptions['open']): Promise<void> => {
  try {
    if (Date.now() < WHATS_NEW_UPDATED_AT) {
      return
    }

    const dismissedAt = await dialogCache.getItem(CACHE_KEY)

    if (!dismissedAt || dismissedAt < WHATS_NEW_UPDATED_AT) {
      open()
    }
  } catch (error) {
    console.error('Error checking dialog state:', error)
  }
}

export default function SourceCentricSummaryOverlay(): JSX.Element | null {
  const [t] = useTranslation()
  const overlay = useOverlay()
  const isSourceCentricOn = !!useSourceCentricDetailsFlag().data?.isOn

  const handleDismissed = useCallback(async () => {
    try {
      // setItem is async and rejects on storage failures, so await it to actually catch them
      await dialogCache.setItem(CACHE_KEY, Date.now())
    } catch (error) {
      console.error('Error saving dialog cache state:', error)
    } finally {
      overlay.close()
    }
  }, [overlay])

  useEffect(() => {
    if (isSourceCentricOn) {
      checkDialogState(overlay.open)
    }
  }, [isSourceCentricOn, overlay.open])

  if (overlay.isOpen || overlay.transitioning)
    return (
      <DialogOverlay title={t('tree-person.scpp-summary-overlay.title')} {...overlay} closeOnClickAway={false}>
        <Paragraph size="sm">
          <Trans
            i18nKey="tree-person.scpp-summary-overlay.description"
            components={{
              L: <Link external target="_blank" to={HELP_ARTICLE_URL} linkName="SCPP Summary: Learn More" />,
            }}
          />
        </Paragraph>

        <Paragraph size="sm">{t('tree-person.scpp-summary-overlay.feedback')}</Paragraph>

        <Separator size="nano" />

        <Expander>
          <ExpanderHeader
            closedTitle={t('tree-person.scpp-summary-overlay.expand-whats-new.action')}
            openedTitle={t('tree-person.scpp-summary-overlay.collapse-whats-new.action')}
            data-testid="scpp-summary-whats-new"
          >
            <H3 size="H6">{t('tree-person.scpp-summary-overlay.whats-new.heading')}</H3>
          </ExpanderHeader>

          <ExpanderContent>
            <ul css={listCss}>
              <li>{t('tree-person.scpp-summary-overlay.list-item-1.description')}</li>
              <li>
                <Trans i18nKey="tree-person.scpp-summary-overlay.list-item-2.description" />
              </li>
              <li>
                <Trans i18nKey="tree-person.scpp-summary-overlay.list-item-3.description" />
              </li>
              <li>
                <Trans i18nKey="tree-person.scpp-summary-overlay.list-item-4.description" />
              </li>
            </ul>
          </ExpanderContent>
        </Expander>

        <DialogOverlayFooter>
          <DialogOverlayActions>
            <Button emphasis="medium" onClick={handleDismissed} data-testid="scpp-summary-dismiss">
              {t('common-ui:dismiss.action')}
            </Button>
          </DialogOverlayActions>
        </DialogOverlayFooter>
      </DialogOverlay>
    )

  return null
}
